import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Wrench } from 'lucide-react';
import {
  getMaintenanceTasks,
  deleteMaintenanceTask,
  updateMaintenanceTaskStatus,
  updateMaintenanceTaskPhotos,
  addMaintenanceWorkNote,
  deleteMaintenanceWorkNote,
  getWorkers,
  assignMaintenanceTask,
} from '@/lib/api';
import { toast } from 'sonner';
import MaintenanceDialog from '@/components/dialogs/MaintenanceDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { MaintenanceTaskCard } from './maintenance/MaintenanceTaskCard';
import { MaintPhotosDialog } from './maintenance/MaintPhotosDialog';
import { MaintWorkNotesDialog } from './maintenance/MaintWorkNotesDialog';
import { PRIORITY_ORDER } from './maintenance/utils';
import { MyTasksToggle, useMyTasksFilter, isMineForTask } from '@/components/MyTasksToggle';
import { useAuth } from '@/contexts/AuthContext';

/**
 * MaintenanceView — list of maintenance tasks sorted by priority.
 * Refactored 2026-Feb: card + dialogs live under `components/maintenance/`
 * so this file is a slim orchestrator (fetch + mutations + layout).
 */
export const MaintenanceView = ({ selectedProperty, properties, onRefresh }) => {
  const { user } = useAuth();
  const [onlyMine, setOnlyMine] = useMyTasksFilter();
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [viewPhotoUrl, setViewPhotoUrl] = useState(null);
  const [newWorkNote, setNewWorkNote] = useState('');
  const [newWorkHours, setNewWorkHours] = useState('');

  // ---- data ----
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMaintenanceTasks(selectedProperty?.id);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching maintenance tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await getWorkers(true);
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchWorkers();
  }, [fetchTasks, fetchWorkers]);

  // ---- mutations ----
  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteMaintenanceTask(taskToDelete.id);
      toast.success('Maintenance task deleted');
      fetchTasks();
      onRefresh?.();
    } catch (_) {
      toast.error('Failed to delete task');
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleStatusChange = async (taskId, status) => {
    try {
      await updateMaintenanceTaskStatus(taskId, status);
      toast.success('Status updated');
      fetchTasks();
    } catch (_) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignWorker = async (taskId, workerId) => {
    try {
      await assignMaintenanceTask(taskId, workerId);
      toast.success('Worker assigned');
      fetchTasks();
    } catch (_) {
      toast.error('Failed to assign worker');
    }
  };

  const handleAddPhoto = async (url) => {
    if (!selectedTask || !url?.trim()) return;
    try {
      const updatedPhotos = [...(selectedTask.photos || []), url.trim()];
      await updateMaintenanceTaskPhotos(selectedTask.id, updatedPhotos);
      toast.success('Photo added');
      fetchTasks();
      setSelectedTask((prev) => ({ ...prev, photos: updatedPhotos }));
    } catch (_) {
      toast.error('Failed to add photo');
    }
  };

  const handleRemovePhoto = async (photoIndex) => {
    if (!selectedTask) return;
    try {
      const updatedPhotos = (selectedTask.photos || []).filter((_, i) => i !== photoIndex);
      await updateMaintenanceTaskPhotos(selectedTask.id, updatedPhotos);
      toast.success('Photo removed');
      fetchTasks();
      setSelectedTask((prev) => ({ ...prev, photos: updatedPhotos }));
    } catch (_) {
      toast.error('Failed to remove photo');
    }
  };

  const handleAddWorkNote = async () => {
    if (!selectedTask || !newWorkNote.trim()) return;
    try {
      const hours = parseFloat(newWorkHours) || 0;
      const result = await addMaintenanceWorkNote(selectedTask.id, newWorkNote.trim(), hours);
      toast.success('Work note added');
      setNewWorkNote('');
      setNewWorkHours('');
      fetchTasks();
      setSelectedTask((prev) => ({
        ...prev,
        work_notes: [
          ...(prev.work_notes || []),
          { note: newWorkNote, hours, timestamp: new Date().toISOString() },
        ],
        hours_worked: result.data.total_hours,
      }));
    } catch (_) {
      toast.error('Failed to add work note');
    }
  };

  const handleDeleteWorkNote = async (noteId) => {
    if (!selectedTask) return;
    try {
      await deleteMaintenanceWorkNote(selectedTask.id, noteId);
      toast.success('Work note deleted');
      fetchTasks();
      setSelectedTask((prev) => ({
        ...prev,
        work_notes: (prev.work_notes || []).filter((n) => n.id !== noteId),
      }));
    } catch (_) {
      toast.error('Failed to delete work note');
    }
  };

  const getPropertyName = useCallback(
    (propertyId) => properties.find((p) => p.id === propertyId)?.name || 'Unknown Property',
    [properties]
  );

  // ---- sort by priority once per tasks change ----
  const sortedTasks = useMemo(
    () => {
      const filtered = onlyMine
        ? tasks.filter((t) => isMineForTask(t, user?.id))
        : tasks;
      return [...filtered].sort(
        (a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99)
      );
    },
    [tasks, onlyMine, user?.id]
  );

  const mineCount = useMemo(
    () => tasks.filter((t) => isMineForTask(t, user?.id)).length,
    [tasks, user?.id]
  );

  const renderCard = (task) => (
    <MaintenanceTaskCard
      key={task.id}
      task={task}
      workers={workers}
      propertyName={getPropertyName(task.property_id)}
      onStatusChange={handleStatusChange}
      onAssignWorker={handleAssignWorker}
      onOpenPhotos={(t) => {
        setSelectedTask(t);
        setPhotoDialogOpen(true);
      }}
      onOpenNotes={(t) => {
        setSelectedTask(t);
        setNotesDialogOpen(true);
      }}
      onDelete={(t) => {
        setTaskToDelete(t);
        setDeleteDialogOpen(true);
      }}
      onPreviewPhoto={setViewPhotoUrl}
    />
  );

  return (
    <div className="space-y-6" data-testid="maintenance-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Maintenance Tasks
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Track repairs and maintenance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MyTasksToggle
            enabled={onlyMine}
            onChange={setOnlyMine}
            count={mineCount}
            testId="maintenance-my-tasks-toggle"
          />
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-maintenance-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : tasks.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Wrench className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No maintenance tasks</h3>
            <p className="text-slate-500 mb-4">Create a maintenance task to track repairs</p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-first-maintenance-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">{sortedTasks.map(renderCard)}</div>
      )}

      {/* Dialogs */}
      <MaintenanceDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          fetchTasks();
          onRefresh?.();
        }}
        properties={properties}
        selectedProperty={selectedProperty}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Maintenance Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{taskToDelete?.title}&rdquo;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-maint-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-maint-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MaintPhotosDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        task={selectedTask}
        onAddUrl={handleAddPhoto}
        onRemove={handleRemovePhoto}
        onPreview={setViewPhotoUrl}
      />

      <MaintWorkNotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        task={selectedTask}
        newWorkNote={newWorkNote}
        setNewWorkNote={setNewWorkNote}
        newWorkHours={newWorkHours}
        setNewWorkHours={setNewWorkHours}
        onAddNote={handleAddWorkNote}
        onDeleteNote={handleDeleteWorkNote}
      />

      {/* Full photo view */}
      <Dialog open={!!viewPhotoUrl} onOpenChange={() => setViewPhotoUrl(null)}>
        <DialogContent className="sm:max-w-[800px] p-2">
          {viewPhotoUrl && (
            <img src={viewPhotoUrl} alt="Full view" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceView;
